import { isRecord } from '@fruitful/fruitils';
import {
    ERRORS_METRIC_ID,
    getThreadErrorTypeDisplayName,
} from '@fruitful/llm-service-shared';
import { type ThreadError, toThreadErrors } from '@fruitful/types';

import { formatToSimpleDateTimeString } from '../../common/utils/formatting';

export const THREAD_CONVERSATION_STATUSES = [
    'Active',
    'Completed',
    'Churn',
] as const;

export type ThreadConversationStatus =
    (typeof THREAD_CONVERSATION_STATUSES)[number];

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

interface ThreadScorerResultMetadata {
    score: number;
}

const isThreadScorerResultMetadata = (
    value: unknown,
): value is ThreadScorerResultMetadata => {
    if (!isRecord(value)) {
        return false;
    }

    return typeof value.score === 'number';
};

const toThreadScorersObject = (
    value: unknown,
): Record<string, unknown> | null => {
    if (!isRecord(value)) {
        return null;
    }

    const hasScorerResult = Object.values(value).some(
        isThreadScorerResultMetadata,
    );

    return hasScorerResult ? value : null;
};

export const getThreadScorerResult = (
    metadata: unknown,
    scorerId: string,
): ThreadScorerResultMetadata | null => {
    if (!isRecord(metadata)) {
        return null;
    }

    const scorers = toThreadScorersObject(metadata.scorers);
    if (scorers == null) {
        return null;
    }

    const result = scorers[scorerId];
    if (!isThreadScorerResultMetadata(result) || !isRecord(result)) {
        return null;
    }

    return {
        score: result.score,
    };
};

const getNestedMetricValue = (
    metrics: Record<string, unknown>,
    fieldId: string,
): unknown => {
    const path = fieldId.split('.');
    let current: unknown = metrics;

    for (const segment of path) {
        if (!isRecord(current)) {
            return null;
        }

        current = current[segment];
    }

    return current;
};

const toThreadMetricsObject = (
    value: unknown,
): Record<string, unknown> | null => {
    if (!isRecord(value)) {
        return null;
    }

    const hasMetricField = Object.entries(value).some(
        ([key, metricValue]) => key !== 'lastCapturedAt' && metricValue != null,
    );

    return hasMetricField ? value : null;
};

export const getThreadMetrics = (
    metadata: unknown,
): Record<string, unknown> | null => {
    if (!isRecord(metadata)) {
        return null;
    }

    return toThreadMetricsObject(metadata.metrics);
};

export const getThreadMetricValue = (
    metadata: unknown,
    metricFieldId: string,
): unknown => {
    const metrics = getThreadMetrics(metadata);
    if (metrics == null) {
        return null;
    }

    // Synthetic "Steps" field: combine the stored completed/total counts into a
    // single "4/5" display value (goals branches only; null otherwise).
    if (metricFieldId === 'stepsProgress') {
        const completed = metrics.stepsCompleted;
        const total = metrics.stepsTotal;
        return typeof completed === 'number' && typeof total === 'number'
            ? `${completed}/${total}`
            : null;
    }

    return getNestedMetricValue(metrics, metricFieldId);
};

/** Typed errors stored on the thread; empty when none are present. */
export const getThreadErrorTypes = (metadata: unknown): ThreadError[] => {
    return toThreadErrors(getThreadMetricValue(metadata, ERRORS_METRIC_ID));
};

/**
 * Whether any error has been captured. Null when the `errors` field is absent.
 */
export const getThreadHasError = (metadata: unknown): boolean | null => {
    const metrics = getThreadMetrics(metadata);
    if (metrics == null || !Array.isArray(metrics[ERRORS_METRIC_ID])) {
        return null;
    }

    return getThreadErrorTypes({ metrics }).length > 0;
};

export const formatThreadMetricValue = (value: unknown): string => {
    if (value == null) {
        return '';
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    if (value instanceof Date) {
        return value.toISOString();
    }

    return JSON.stringify(value);
};

const formatConversationDurationMs = (ms: number): string => {
    if (ms < 1000) {
        return `${Math.round(ms)}ms`;
    }

    const seconds = ms / 1000;
    if (seconds < 60) {
        return `${seconds.toFixed(1)}s`;
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    if (minutes < 60) {
        return `${minutes}m ${remainingSeconds}s`;
    }

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
};

export const formatThreadMetricFieldValue = (
    metricFieldId: string,
    value: unknown,
): string => {
    if (
        (metricFieldId === 'conversationDurationMs' ||
            metricFieldId === 'agentResponseWaitMs') &&
        typeof value === 'number'
    ) {
        return formatConversationDurationMs(value);
    }

    if (metricFieldId === 'lastMessageAt' && typeof value === 'string') {
        return formatToSimpleDateTimeString(value);
    }

    if (metricFieldId === ERRORS_METRIC_ID && Array.isArray(value)) {
        const errorTypes = toThreadErrors(value);
        if (errorTypes.length === 0) {
            return 'No';
        }

        return errorTypes.map(getThreadErrorTypeDisplayName).join(', ');
    }

    return formatThreadMetricValue(value);
};

const hasAhaMomentMetrics = (metrics: Record<string, unknown>): boolean => {
    const ahaMoment = metrics.ahaMoment;
    if (!isRecord(ahaMoment)) {
        return false;
    }

    return (
        (typeof ahaMoment.headline === 'string' &&
            ahaMoment.headline.trim() !== '') ||
        (typeof ahaMoment.goalName === 'string' &&
            ahaMoment.goalName.trim() !== '') ||
        (typeof ahaMoment.date === 'string' && ahaMoment.date.trim() !== '')
    );
};

const isLastMessageWithinOneDay = (lastMessageAt: unknown): boolean => {
    if (typeof lastMessageAt !== 'string' || lastMessageAt === '') {
        return false;
    }

    const lastMessageTime = Date.parse(lastMessageAt);
    if (Number.isNaN(lastMessageTime)) {
        return false;
    }

    return Date.now() - lastMessageTime <= ONE_DAY_MS;
};

const toActivityTimestamp = (
    lastMessageAt: unknown,
    updatedAt?: Date | string | null,
): string | null => {
    if (typeof lastMessageAt === 'string' && lastMessageAt !== '') {
        return lastMessageAt;
    }

    if (updatedAt instanceof Date) {
        return updatedAt.toISOString();
    }

    if (typeof updatedAt === 'string' && updatedAt !== '') {
        return updatedAt;
    }

    return null;
};

/**
 * Client-only conversation status derived from captured metrics.
 * Completed if an aha moment was shown; Active if last activity was within a day;
 * otherwise Churn. Falls back to thread `updatedAt` when metrics lack `lastMessageAt`.
 */
export const getThreadConversationStatus = (
    metadata: unknown,
    updatedAt?: Date | string | null,
): ThreadConversationStatus | null => {
    const metrics = getThreadMetrics(metadata);
    if (metrics == null) {
        return null;
    }

    if (hasAhaMomentMetrics(metrics)) {
        return 'Completed';
    }

    if (
        isLastMessageWithinOneDay(
            toActivityTimestamp(metrics.lastMessageAt, updatedAt),
        )
    ) {
        return 'Active';
    }

    return 'Churn';
};

const WORKING_MEMORY_METADATA_KEY = 'workingMemory';

const hasWorkingMemoryContent = (value: Record<string, unknown>): boolean =>
    Object.values(value).some((field) => {
        if (field == null || field === '') {
            return false;
        }

        if (Array.isArray(field)) {
            return field.length > 0;
        }

        return true;
    });

/**
 * Thread-scoped working memory snapshot from `metadata.workingMemory`.
 * Schema-based memory is an object (or JSON string); template memory is markdown.
 */
export const getThreadWorkingMemory = (
    metadata: unknown,
): string | Record<string, unknown> | null => {
    if (!isRecord(metadata)) {
        return null;
    }

    const value = metadata[WORKING_MEMORY_METADATA_KEY];
    if (value == null || value === '') {
        return null;
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
            return null;
        }

        try {
            const parsed: unknown = JSON.parse(trimmed);
            if (isRecord(parsed)) {
                return hasWorkingMemoryContent(parsed) ? parsed : null;
            }
        } catch {
            return trimmed;
        }

        return trimmed;
    }

    if (isRecord(value) && hasWorkingMemoryContent(value)) {
        return value;
    }

    return null;
};

export const formatWorkingMemoryFieldValue = (value: unknown): string => {
    if (value == null || value === '') {
        return '';
    }

    if (Array.isArray(value)) {
        return value
            .filter((item): item is string => typeof item === 'string')
            .join(', ');
    }

    return formatThreadMetricValue(value);
};
